import { isAutoTypeCheckRule } from "@/lib/schemaBuilderHelpers";
import { SCHEMA_RULE_CATEGORIES } from "@/types/schemaBuilder";
import type { SchemaRule, SchemaRuleCategory } from "@/types/schemaBuilder";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { SchemaRuleCard } from "./SchemaRuleCard";

interface SchemaBuilderRulesTabProps {
  rules: SchemaRule[];
  conflictCount: number;
  onAddRule: () => void;
  onEditRule: (rule: SchemaRule) => void;
  onDeleteRule: (rule: SchemaRule) => void;
}

const RULE_TYPE_OPTIONS = SCHEMA_RULE_CATEGORIES;

export function SchemaBuilderRulesTab({
  rules,
  conflictCount,
  onAddRule,
  onEditRule,
  onDeleteRule,
}: SchemaBuilderRulesTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [ruleTypeFilter, setRuleTypeFilter] = useState<SchemaRuleCategory | "All">("All");
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);

  const schemaRules = useMemo(
    () => rules.filter((rule) => (rule.scope ?? "column") === "schema"),
    [rules]
  );

  const ruleTypeCounts = useMemo(() => {
    return {
      arithmetic: schemaRules.filter((rule) => rule.category === "Arithmetic").length,
      ifThen: schemaRules.filter((rule) => rule.category === "If-Then").length,
      chronological: schemaRules.filter((rule) => rule.category === "Chronological").length,
      syntactical: schemaRules.filter((rule) => rule.category === "Syntactical").length,
    };
  }, [schemaRules]);

  const filteredRules = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return schemaRules.filter((rule) => {
      const matchesSearch =
        !query ||
        rule.title.toLowerCase().includes(query) ||
        rule.ruleKey.toLowerCase().includes(query) ||
        rule.formula.toLowerCase().includes(query) ||
        rule.description.toLowerCase().includes(query);

      const matchesType = ruleTypeFilter === "All" || rule.category === ruleTypeFilter;
      const matchesConflicts = !showConflictsOnly || rule.hasConflict;

      return matchesSearch && matchesType && matchesConflicts;
    });
  }, [schemaRules, searchQuery, ruleTypeFilter, showConflictsOnly]);

  return (
    <>
      <p className="mb-6 text-sm text-[var(--color-text-primary)]">
        Any rules that have dependencies or span multiple columns can be viewed and edited here.
      </p>

      <div className="mb-4 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-[220px] items-center gap-2 rounded-md border border-[var(--color-border)] bg-white px-3">
            <Search className="h-4 w-4 text-[var(--color-text-tertiary)]" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search rules..."
              className="w-full text-sm outline-none placeholder:text-[var(--color-text-tertiary)]"
            />
          </div>

          <select
            value={ruleTypeFilter}
            onChange={(event) =>
              setRuleTypeFilter(event.target.value as SchemaRuleCategory | "All")
            }
            className="h-9 w-[190px] rounded-md border border-[var(--color-border)] bg-white px-3 text-sm text-[var(--color-text-secondary)] outline-none focus:border-[var(--color-action-primary)]"
          >
            {RULE_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                Rule Type: {option}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setShowConflictsOnly((prev) => !prev)}
            className={`h-9 whitespace-nowrap rounded-md border px-4 text-sm transition ${
              showConflictsOnly
                ? "border-[var(--color-action-primary)] bg-green-50 text-[var(--color-action-primary)]"
                : "border-[var(--color-border)] bg-white text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
            }`}
          >
            Has Conflicts ({conflictCount})
          </button>
        </div>

        <div className="flex items-center gap-5">
          <div className="flex items-center gap-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
            <span>{ruleTypeCounts.arithmetic} arithmetic</span>
            <span>{ruleTypeCounts.ifThen} if-then</span>
            <span>{ruleTypeCounts.chronological} chronological</span>
            <span>{ruleTypeCounts.syntactical} syntactical</span>
          </div>

          <button
            type="button"
            onClick={onAddRule}
            className="whitespace-nowrap rounded-md bg-[var(--color-action-primary)] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)]"
          >
            Add Rule
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {filteredRules.map((rule) => (
          <SchemaRuleCard
            key={rule.id}
            rule={rule}
            isLocked={isAutoTypeCheckRule(rule)}
            onEdit={onEditRule}
            onDelete={onDeleteRule}
          />
        ))}

        {filteredRules.length === 0 && (
          <div className="rounded-md border border-[var(--color-border-light)] bg-white p-6 text-sm text-[var(--color-text-secondary)]">
            No rules match the current filters.
          </div>
        )}
      </div>
    </>
  );
}
